/**
 * @file DraggableHandle Atom Component
 * @description 드래그 앤 드롭 핸들 컴포넌트 (dnd-kit 통합)
 * @see docs/plans/loading/05-implementation.md - Section 34
 */

import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { ReactComponent as DragIcon } from '@/src/assets/icons/Drag.svg';

export interface DraggableHandleProps {
  /** 드래그 리스너 (dnd-kit에서 제공) */
  listeners?: ReturnType<typeof useSortable>['listeners'];
  /** 드래그 속성 (dnd-kit에서 제공) */
  attributes?: ReturnType<typeof useSortable>['attributes'];
  /** 비활성화 상태 */
  disabled?: boolean;
  /** 드래그 중 상태 */
  isDragging?: boolean;
  /** 추가 CSS 클래스 */
  className?: string;
}

/**
 * 드래그 핸들 스타일
 * - 6개의 점으로 구성된 그리드 아이콘
 * - hover 시 배경색 변경
 * - disabled 시 투명도 감소
 */
const HandleWrapper = styled.button<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'grab')};
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  border-radius: 4px;
  transition: background-color 0.2s ease;
  touch-action: none;

  &:active:not(:disabled) {
    cursor: grabbing;
  }

  &:focus-visible {
    outline: 2px solid #1890ff;
    outline-offset: 2px;
  }
`;

/**
 * 드래그 아이콘 스타일 (드래그 시 검정색으로 변경)
 */
const StyledDragIcon = styled(DragIcon)<{ $isDragging?: boolean }>`
  path {
    fill: ${({ $isDragging }) => ($isDragging ? '#5E696E' : '#E7EDF0')};
    transition: fill 0.2s ease;
  }
`;

/**
 * DraggableHandle - 드래그 핸들 컴포넌트
 *
 * @description
 * dnd-kit과 통합되어 리스트 아이템의 드래그 앤 드롭을 가능하게 합니다.
 *
 * @example
 * ```tsx
 * const SortableItem = ({ id }) => {
 *   const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
 *
 *   return (
 *     <div ref={setNodeRef} style={{ transform, transition }}>
 *       <DraggableHandle attributes={attributes} listeners={listeners} />
 *       <span>Item content</span>
 *     </div>
 *   );
 * };
 * ```
 */
export const DraggableHandle = forwardRef<HTMLButtonElement, DraggableHandleProps>(
  ({ listeners, attributes, disabled = false, isDragging = false, className }, ref) => {
    const { t } = useTranslation(['common']);

    return (
      <HandleWrapper
        ref={ref}
        type="button"
        className={className}
        $disabled={disabled}
        disabled={disabled}
        aria-label={t('common:dragToReorder')}
        {...attributes}
        {...listeners}
      >
        <StyledDragIcon $isDragging={isDragging} />
      </HandleWrapper>
    );
  }
);

DraggableHandle.displayName = 'DraggableHandle';

export default DraggableHandle;
